<template>
    <div>
        <form @submit.prevent="onSubmit">
            <v-layout justify-start align-end>
                <h2 v-if="label" class="text-h6 font-weight-normal mr-2 mb-1">{{ label }}</h2>
                <slot name="header-actions" />
            </v-layout>

            <template v-if="Array.isArray(formData)">
                <div v-for="(item, index) in formData" :key="index" class="rounded mb-4 background darken-2">
                    <div class="d-flex justify-end pa-1">
                        <app-button
                            small
                            :tooltip="$t('remove')"
                            :icon="getIcon('delete', 'fas')"
                            :disabled="disabled"
                            @click="formData.splice(index, 1)"
                        />
                    </div>
                    <div v-for="field in schema" :key="field.name" class="px-6">
                        <component
                            :is="`Field${field.type}`"
                            :ref="`input.${field.name}`"
                            v-model="formData[index][field.name]"
                            :name="field.name"
                            :label="field.label"
                            :validation="!disabled && !readonly && Array.isArray(field.rules) ? field.rules : []"
                            :mask="field.mask"
                            :disabled="disabled"
                            :readonly="readonly"
                            :enums="field.type === DynamicFormTypeEnum.ENUM ? field.schema : null"
                            :default-value="field.type === DynamicFormTypeEnum.BOOLEAN ? false : null"
                        />
                    </div>
                </div>
            </template>

            <template v-else>
                <div v-for="field in schema" :key="field.name">
                    <template v-if="field.type === DynamicFormTypeEnum.ARRAY">
                        <dynamic-form
                            v-model="formData[field.name]"
                            :default-value="[]"
                            hide-actions
                            :label="field.label"
                            :schema="field.schema"
                            :validation="Array.isArray(field.rules) ? field.rules : []"
                            :disabled="disabled"
                            :readonly="readonly"
                        >
                            <template #header-actions>
                                <app-button
                                    secondary
                                    small
                                    :text="$t('create')"
                                    icon-size="1rem"
                                    icon-left="add"
                                    :disabled="disabled || (max !== null && formData.length >= max)"
                                    class="mb-1"
                                    @click="formData[field.name].unshift(getDefaultItem(field))"
                                />
                            </template>
                        </dynamic-form>
                    </template>
                    <component
                        :is="`Field${field.type}`"
                        v-else
                        :ref="`input.${field.name}`"
                        v-model="formData[field.name]"
                        :name="field.name"
                        :label="field.label"
                        :validation="!disabled && !readonly ? field.rules : []"
                        :mask="field.mask"
                        :disabled="disabled"
                        :readonly="readonly"
                        :enums="field.type === DynamicFormTypeEnum.ENUM ? field.schema : null"
                        :default-value="field.type === DynamicFormTypeEnum.BOOLEAN ? false : null"
                    />
                </div>
            </template>

            <v-input v-if="!readonly" ref="input-error" v-model="formData" :rules="rules" class="mb-2" />

            <v-layout
                v-if="!readonly && !hideActions"
                class="app-form__actions primaryLight mt-4 py-4 px-5 rounded justify-end"
            >
                <app-button
                    :loading="loading"
                    :disabled="disabled"
                    :title="$t('submit')"
                    :text="$t('submit')"
                    :icon="getIcon('save')"
                    @click="onSubmit"
                />
            </v-layout>
        </form>
    </div>
</template>

<script>
import DynamicFormTypeEnum from '@/enums/dynamicForm/type';
import validate from '@/mixins/validate';

export default {
    name: 'DynamicForm',
    mixins: [validate],
    props: {
        value: {
            type: [Object, Array],
            default: () => ({})
        },
        defaultValue: {
            type: [Object, Array],
            default: () => ({})
        },
        schema: {
            type: Array,
            required: true
        },
        label: {
            type: String,
            default: null
        },
        loading: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        readonly: {
            type: Boolean,
            default: false
        },
        hideActions: {
            type: Boolean,
            default: false
        },
        validation: {
            type: Array,
            default: () => []
        }
    },
    data: () => ({
        DynamicFormTypeEnum,
        formData: {}
    }),
    computed: {
        rules() {
            const rules = [];

            if (this.validation && this.validation.length) {
                this.validation.forEach(rule => {
                    const { validator, args } = this.getValidatorFromString(rule);
                    rules.push(() => validator(this.formData, this.label, ...args));
                });
            }

            if (this.required && !this.validation.some(rule => rule.startsWith('required'))) {
                rules.push(() => this.getValidator('required')(this.formData, this.label));
            }

            return rules;
        },
        max() {
            const maxRule = this.validation.find(rule => rule.startsWith('max:'));
            return maxRule ? parseInt(maxRule.split(':')[1], 10) : null;
        },
        min() {
            const minRule = this.validation.find(rule => rule.startsWith('min:'));
            return minRule ? parseInt(minRule.split(':')[1], 10) : null;
        },
        required() {
            return this.validation.some(rule => rule.startsWith('required'));
        }
    },
    watch: {
        formData: {
            deep: true,
            handler() {
                this.$emit('input', this.formData);
            }
        }
    },
    created() {
        if (this.value) {
            if (Array.isArray(this.value) && this.value.length) {
                this.formData = this.value;
            } else if (typeof this.value === 'object' && Object.keys(this.value).length) {
                this.formData = this.value;
            } else {
                this.formData = this.defaultValue;
            }
        } else {
            this.formData = this.defaultValue;

            for (const field of this.schema) {
                if (field.value) {
                    this.formData[field.name] = field.value;
                }
            }
        }
    },
    methods: {
        onSubmit() {
            if (this.validate()) {
                this.$emit('submit', this.formData);
            } else {
                this.$emit('error', this.formData);
            }
        },
        getDefaultItem(field) {
            return field.schema.reduce((obj, fieldSchema) => {
                obj[fieldSchema.name] = null;
                return obj;
            }, {});
        }
    }
};
</script>
